import axios from 'axios';
import { getFirebaseToken } from './firebase';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(async (config) => {
  let token = await getFirebaseToken();
  if (!token) token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Only redirect if not already on login/register/accept-invite pages
      const path = window.location.pathname;
      if (!['/login', '/register', '/accept-invite'].some(p => path.startsWith(p))) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  firebaseRegister: (data) => api.post('/auth/firebase-register', data),
  getMe: () => api.get('/auth/me'),
};

// Organisation APIs
export const organisationAPI = {
  getMe: () => api.get('/organisations/me'),
  update: (data) => api.put('/organisations/me', data),
};

// Invitation APIs
export const invitationAPI = {
  create: (data) => api.post('/invitations/', data),
  list: () => api.get('/invitations/'),
  revoke: (id) => api.delete(`/invitations/${id}`),
  verify: (token) => api.get(`/invitations/verify/${token}`),
  accept: (data) => api.post('/invitations/accept', data),
};

// Property APIs
export const propertyAPI = {
  getAll: (params) => api.get('/properties/', { params }),
  getById: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties/', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
  uploadImage: (id, data) => api.post(`/properties/${id}/images`, data),
  deleteImage: (id, imageId) => api.delete(`/properties/${id}/images/${imageId}`),
};

// Unit APIs
export const unitAPI = {
  getAll: (propertyId) => api.get('/units/', { params: { property_id: propertyId } }),
  getById: (id) => api.get(`/units/${id}`),
  create: (data) => api.post('/units/', data),
  update: (id, data) => api.put(`/units/${id}`, data),
  uploadImage: (id, data) => api.post(`/units/${id}/images`, data),
  deleteImage: (id, imageId) => api.delete(`/units/${id}/images/${imageId}`),
};

// Tenant APIs
export const tenantAPI = {
  getAll: () => api.get('/tenants/'),
  getById: (id) => api.get(`/tenants/${id}`),
};

// Lease APIs
export const leaseAPI = {
  getAll: () => api.get('/leases/'),
  getById: (id) => api.get(`/leases/${id}`),
  create: (data) => api.post('/leases/', data),
  activate: (id) => api.put(`/leases/${id}/activate`),
  renew: (id, data) => api.post(`/leases/${id}/renew`, data),
  getExpiring: (days = 60) => api.get('/leases/expiring/upcoming', { params: { days } }),
};

// Payment APIs
export const paymentAPI = {
  getAll: () => api.get('/payments/'),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments/', data),
  generateMonthly: () => api.post('/payments/generate-monthly'),
  createCheckout: (paymentId) => api.post('/payments/checkout', null, {
    params: { payment_id: paymentId, origin_url: window.location.origin },
  }),
  getCheckoutStatus: (sessionId) => api.get(`/payments/checkout/status/${sessionId}`),
};

// Maintenance APIs
export const maintenanceAPI = {
  getAll: () => api.get('/maintenance/'),
  getById: (id) => api.get(`/maintenance/${id}`),
  create: (data) => api.post('/maintenance/', data),
  updateStatus: (id, status) => api.put(`/maintenance/${id}/status`, null, { params: { status } }),
  addNote: (id, note) => api.post(`/maintenance/${id}/notes`, null, { params: { note } }),
  addMessage: (id, text) => api.post(`/maintenance/${id}/messages`, { text }),
  getMessages: (id) => api.get(`/maintenance/${id}/messages`),
  submitSurvey: (id, data) => api.post(`/maintenance/${id}/survey`, data),
  getSurvey: (id) => api.get(`/maintenance/${id}/survey`),
};

// Admin APIs
export const adminAPI = {
  getAllLandlords: () => api.get('/admin/landlords'),
  getStats: () => api.get('/admin/stats'),
  getOrganisations: () => api.get('/admin/organisations'),
  suspendOrg: (orgId) => api.put(`/admin/organisations/${orgId}/suspend`),
  activateOrg: (orgId) => api.put(`/admin/organisations/${orgId}/activate`),
  updateOrgSubscription: (orgId, tier, status) => api.put(`/admin/organisations/${orgId}/subscription`, null, { params: { tier, status } }),
  updateSubscription: (landlordId, tier) => api.put(`/admin/landlords/${landlordId}/subscription`, null, { params: { tier } }),
  seedData: () => api.post('/api/seed'),
  getPayments: (status) => api.get('/admin/payments', { params: status ? { status } : {} }),
  impersonate: (landlordId) => api.get(`/admin/impersonate/${landlordId}`),
};

// Stripe Connect APIs
export const connectAPI = {
  getStatus: () => api.get('/connect/status'),
  createOnboarding: () => api.post('/connect/onboard', null, {
    params: { origin_url: window.location.origin },
  }),
  verify: () => api.post('/connect/verify'),
  refreshStatus: () => api.post('/connect/refresh-status'),
  disconnect: () => api.delete('/connect/disconnect'),
};

// Subscription APIs - Updated
export const subscriptionAPI = {
  getPlans: () => api.get('/subscriptions/plans'),
  getMyStatus: () => api.get('/subscriptions/my-status'),
  checkLimits: () => api.get('/subscriptions/check-limits'),
  previewChange: (tier) => api.post('/subscriptions/preview-change', null, {
    params: { tier },
  }),
  checkout: (tier, originUrl) => api.post('/subscriptions/checkout', null, {
    params: { tier, origin_url: originUrl || window.location.origin },
  }),
  createCheckout: (tier) => api.post('/subscriptions/checkout', null, {
    params: { tier, origin_url: window.location.origin },
  }),
  changePlan: (tier) => api.post('/subscriptions/change-plan', null, {
    params: { tier, origin_url: window.location.origin },
  }),
  getStatus: (sessionId) => api.get(`/subscriptions/status/${sessionId}`),
  cancel: () => api.post('/subscriptions/cancel'),
  getPortal: () => api.get('/subscriptions/portal'),
};

// Analytics APIs
export const analyticsAPI = {
  getLandlordAnalytics: (params) => api.get('/analytics/landlord', { params }),
  getAdminAnalytics: () => api.get('/analytics/admin'),
};

// Preferences APIs
export const preferencesAPI = {
  get: () => api.get('/preferences/'),
  update: (data) => api.put('/preferences/', data),
};

// Notification APIs
export const notificationAPI = {
  getAll: (unreadOnly) => api.get('/notifications/', { params: { unread_only: unreadOnly || false } }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Document APIs
export const documentAPI = {
  upload: (data) => api.post('/documents/upload', data),
  getByLease: (leaseId) => api.get(`/documents/lease/${leaseId}`),
  download: (filePath) => api.get(`/documents/download/${filePath}`, { responseType: 'blob' }),
  delete: (docId, leaseId) => api.delete(`/documents/${docId}`, { params: { lease_id: leaseId } }),
  myDocuments: () => api.get('/documents/my-documents'),
  tenantDocuments: (tenantId) => api.get(`/documents/tenant/${tenantId}`),
  getReceipt: (paymentId) => api.get(`/payments/${paymentId}/receipt`),
};

// Reports APIs (landlord)
export const reportsAPI = {
  getRentRoll: (month) => api.get('/reports/rent-roll', { params: { month } }),
  exportRentRoll: (month, format) => api.get('/reports/rent-roll/export', { params: { month, format }, responseType: 'blob' }),
  getPaymentHistory: (start_date, end_date) => api.get('/reports/payment-history', { params: { start_date, end_date } }),
  exportPaymentHistory: (start_date, end_date, format) => api.get('/reports/payment-history/export', { params: { start_date, end_date, format }, responseType: 'blob' }),
};

// Export APIs
export const exportAPI = {
  payments: () => api.get('/export/payments', { responseType: 'blob' }),
  tenants: () => api.get('/export/tenants', { responseType: 'blob' }),
  adminPayments: () => api.get('/export/admin/payments', { responseType: 'blob' }),
};

export default api;
